"use client";

// This page needs to be dynamically rendered to avoid prerendering issues
export const dynamic = "force-dynamic";

import { useMemo, useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Heading } from "@/components/ui/typography";

export default function DocsHomePage() {
  const t = useTranslations("docs");
  const [mounted, setMounted] = useState(false);
  const { data: session, status } = useSession();
  const searchParams = useSearchParams();

  const isDev = process.env.NODE_ENV !== "production";
  const h1w = searchParams?.get("h1w");
  const h1WeightClass = useMemo(() => {
    if (!isDev) return undefined;
    switch ((h1w || "").toLowerCase()) {
      case "bold":
        return "font-bold";
      case "semibold":
        return "font-semibold";
      case "medium":
        return "font-medium";
      default:
        return undefined;
    }
  }, [h1w, isDev]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // 设置页面标题
  useEffect(() => {
    if (!mounted) return;

    const title = t("breadcrumb.docs");
    if (title) {
      const pageTitle = `${title} | TalkMaster`;
      document.title = pageTitle;
    }
  }, [t, mounted]);

  // 如果组件还未挂载或会话正在加载，显示加载状态
  if (!mounted || status === "loading") {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-12 px-4">
      <Breadcrumb
        items={[
          { label: t("breadcrumb.home"), href: "/" },
          { label: t("breadcrumb.docs"), href: "/docs" },
        ]}
      />

      <div className="mt-8">
        <Heading
          variant="h1"
          as="h1"
          className={`mb-6 font-semibold ${h1WeightClass ?? ""}`}
        >
          {t("title")}
        </Heading>
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-lg text-gray-700 dark:text-gray-300">
            {t("description")}
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-3">
                {t("gettingStarted.title")}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t("gettingStarted.description")}
              </p>
              <Link
                href="/docs/getting-started"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {t("gettingStarted.cta")} →
              </Link>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-3">
                {t("apiReference.title")}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t("apiReference.description")}
              </p>
              <Link
                href="/docs/api"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {t("apiReference.cta")} →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
